import type { GridApi, IRowNode } from "ag-grid-community";
import type { RefObject } from "react";

import type { ColumnRow } from "../ColumnSelectionGrid.tsx";
import { forEachRowNodeInStableOrder } from "./gridModel.ts";

// Programmatically select the provided column ids, clearing any stale selection beforehand.
export function setSelection(gridApi: RefObject<GridApi<ColumnRow> | null>, colIds: string[]) {
  const nodesToSelect: IRowNode[] = [];

  for (const colId of colIds) {
    const node = gridApi.current?.getRowNode(colId);

    if (node) {
      nodesToSelect.push(node);
    }
  }

  gridApi.current?.deselectAll();
  gridApi.current?.setNodesSelected({ nodes: nodesToSelect, newValue: true });
}

// Reads the current selection in display order so templates can persist the exact column sequence.
export function getOrderedSelectedColumnIds(api: GridApi<ColumnRow> | null | undefined): string[] {
  if (!api) {
    return [];
  }

  const selectedIds: string[] = [];

  const appendIfSelected = (node: IRowNode<ColumnRow>) => {
    if (node.group || node.data?.fixed || node.data?.isGroup) {
      return;
    }

    const colId = node.data?.colId;
    if (colId && node.isSelected()) {
      selectedIds.push(colId);
    }
  };

  api.forEachPinnedRow("top", appendIfSelected);

  const walked = forEachRowNodeInStableOrder(api, appendIfSelected);

  if (!walked) {
    // Fallback for unexpected row models.
    api.forEachNode(appendIfSelected);
  }

  return selectedIds;
}
